import { useMemo } from "react";

import { formatStepDuration } from "src/domains/application/hooks/pipeline-graph-from-state-helpers";
import type { PipelineStep } from "src/domains/application/hooks/pipeline-graph-from-state-helpers";

import {
  type DagNodeData,
  type DagPhase,
  type DagStatus,
  PIPELINE_MODE_PHASES,
  PHASE_ORDER,
  PHASE_LABELS,
  toDagStatus,
} from "../pages/StackDetailsPage/components/dag-types";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DagNode {
  id: string;
  data: DagNodeData;
}

export interface DagEdge {
  id: string;
  source: string;
  target: string;
  data: { status: DagStatus };
}

export interface UsePipelineDagResult {
  nodes: DagNode[];
  edges: DagEdge[];
  /** Distinct phases present, ordered left → right. */
  phases: DagPhase[];
  /** Distinct service names (excluding phase-level rows). */
  serviceNames: string[];
  /** True when canonical DAG data (pipelineMode + templateServices) is still loading. */
  loading: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Derive the canonical phase key from a PipelineStep.step value.
 */
function stepToPhase(step: string): DagPhase | null {
  switch (step) {
    case "build":
      return "build";
    case "security_scan":
    case "scan":
      return "security_scan";
    case "quality_gate":
      return "quality_gate";
    case "deploy":
      return "deploy";
    case "verify":
      return "verify";
    case "vcs_provisioning":
    case "vcs":
      return "vcs_provisioning";
    default:
      return null;
  }
}

/** Build a stable node id from phase + service. */
function nodeId(phase: DagPhase, service: string): string {
  return service ? `${phase}:${service}` : phase;
}

/**
 * Compute the edges between nodes based on phase ordering and service matching.
 *
 * Rules (from the contract):
 *  - build:svc → security_scan:svc (same service)
 *  - security_scan:svc → deploy:svc (same service)
 *  - deploy:svc → verify (all deploys → single verify node)
 *  - If a phase is absent, edges connect across the phases that ARE present.
 */
function buildEdges(
  phases: DagPhase[],
  servicesByPhase: Map<DagPhase, Map<string, { status: DagStatus }>>,
): DagEdge[] {
  const edges: DagEdge[] = [];

  for (let i = 0; i < phases.length - 1; i++) {
    const cur = phases[i];
    const next = phases[i + 1];
    const curServices = servicesByPhase.get(cur);
    const nextServices = servicesByPhase.get(next);
    if (!curServices || !nextServices) continue;

    // Special case: next phase is verify → all current nodes connect to verify.
    if (next === "verify") {
      const verifySvc = nextServices.has("")
        ? ""
        : (nextServices.keys().next().value ?? "");
      const verifyStatus = nextServices.get(verifySvc)?.status ?? "pending";
      for (const [svc] of curServices) {
        edges.push({
          id: `e:${nodeId(cur, svc)}:${nodeId(next, verifySvc)}`,
          source: nodeId(cur, svc),
          target: nodeId(next, verifySvc),
          data: { status: verifyStatus },
        });
      }
      continue;
    }

    // General case: connect same-service nodes across consecutive phases.
    const nextHasServices = [...nextServices.keys()].some((k) => k !== "");
    for (const [svc] of curServices) {
      if (nextHasServices) {
        const nextInfo = nextServices.get(svc);
        if (!nextInfo) continue;
        edges.push({
          id: `e:${nodeId(cur, svc)}:${nodeId(next, svc)}`,
          source: nodeId(cur, svc),
          target: nodeId(next, svc),
          data: { status: nextInfo.status },
        });
      } else {
        const targetSvc = [...nextServices.keys()][0] ?? "";
        const targetStatus = nextServices.get(targetSvc)?.status ?? "pending";
        edges.push({
          id: `e:${nodeId(cur, svc)}:${nodeId(next, targetSvc)}`,
          source: nodeId(cur, svc),
          target: nodeId(next, targetSvc),
          data: { status: targetStatus },
        });
      }
    }
  }

  return edges;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * usePipelineDag — transforms PipelineStep[] into plain DAG node/edge data
 * for the per-service pipeline visualization.
 *
 * When `pipelineMode` and `templateServices` are provided, the DAG structure
 * (phases + service nodes) is built from those — ensuring a consistent shape
 * across all runs of the same stack. Per-run step rows only update node
 * statuses and durations; they do not add or remove nodes.
 *
 * Falls back to the data-driven approach (deriving structure from step rows)
 * when canonical info is unavailable.
 */
export function usePipelineDag(
  pipelineState: PipelineStep[] = [],
  templateServices?: { name: string; type: string }[],
  pipelineMode?: string,
  intent?: string,
): UsePipelineDagResult {
  return useMemo(() => {
    // ── Group step rows by phase → service (for status/duration merge) ────
    const servicesByPhase = new Map<
      DagPhase,
      Map<string, { status: DagStatus; row: PipelineStep }>
    >();
    for (const row of pipelineState) {
      const phase = stepToPhase(row.step);
      if (!phase) continue;
      if (!servicesByPhase.has(phase)) servicesByPhase.set(phase, new Map());
      const map = servicesByPhase.get(phase)!;
      const existing = map.get(row.serviceName);
      if (
        !existing ||
        new Date(row.updatedAt) > new Date(existing.row.updatedAt)
      ) {
        map.set(row.serviceName, { status: toDagStatus(row.status), row });
      }
    }

    // Derive template service types for label badges.
    const typeByName = new Map<string, string>();
    for (const svc of templateServices ?? [])
      typeByName.set(svc.name, svc.type);

    // ── Determine canonical phases + services ────────────────────────────
    // For deploy-only runs (intent "deploy"), only show deploy + verify
    // phases — build and scan didn't run.
    const basePhases = pipelineMode
      ? (PIPELINE_MODE_PHASES[pipelineMode] ?? [])
      : [];
    const canonicalPhases =
      intent === "deploy"
        ? basePhases.filter((p) => p === "deploy" || p === "verify")
        : basePhases;
    const canonicalServiceNames = (templateServices ?? []).map((s) => s.name);

    const useCanonical =
      canonicalPhases.length > 0 &&
      templateServices !== undefined &&
      canonicalServiceNames.length > 0;

    // Loading state: canonical mode is possible but templateServices hasn't
    // loaded yet. Return empty with loading=true so the UI shows a skeleton.
    if (canonicalPhases.length > 0 && templateServices === undefined) {
      return {
        nodes: [],
        edges: [],
        phases: [],
        serviceNames: [],
        loading: true,
      };
    }

    let phases: DagPhase[];
    let serviceNames: Set<string>;

    if (useCanonical) {
      phases = canonicalPhases;
      serviceNames = new Set(canonicalServiceNames);
    } else {
      if (!pipelineState || pipelineState.length === 0) {
        return {
          nodes: [],
          edges: [],
          phases: [],
          serviceNames: [],
          loading: false,
        };
      }
      phases = PHASE_ORDER.filter((p: DagPhase) => servicesByPhase.has(p));
      serviceNames = new Set<string>();
      for (const [, map] of servicesByPhase) {
        for (const name of map.keys()) if (name) serviceNames.add(name);
      }
    }

    // ── Build nodes ──────────────────────────────────────────────────────
    const nodes: DagNode[] = [];
    const nodeStatusMap = new Map<
      DagPhase,
      Map<string, { status: DagStatus }>
    >();

    for (const phase of phases) {
      const stepMap = servicesByPhase.get(phase);
      const inner = new Map<string, { status: DagStatus }>();
      nodeStatusMap.set(phase, inner);

      const isPhaseLevel = phase === "verify";

      if (isPhaseLevel) {
        const stepRow = stepMap?.get("");
        const status = stepRow?.status ?? "pending";
        const row = stepRow?.row;
        const duration = row ? (formatStepDuration(row) ?? null) : null;
        const message = row?.message ?? "";
        inner.set("", { status });
        nodes.push({
          id: nodeId(phase, ""),
          data: {
            phase,
            service: "",
            status,
            type: "",
            duration,
            label: PHASE_LABELS[phase],
            message,
            executions: row?.executions ?? [],
          },
        });
        continue;
      }

      // Per-service nodes.
      const svcList = useCanonical ? canonicalServiceNames : [...serviceNames];
      const phaseLevelRow = useCanonical ? stepMap?.get("") : undefined;
      for (const svc of svcList) {
        if (!svc) continue;
        const stepRow = stepMap?.get(svc) ?? phaseLevelRow;
        const status = stepRow?.status ?? "pending";
        const row = stepRow?.row;
        const duration = row ? (formatStepDuration(row) ?? null) : null;
        const message = row?.message ?? "";
        const type = typeByName.get(svc) ?? "";
        inner.set(svc, { status });
        nodes.push({
          id: nodeId(phase, svc),
          data: {
            phase,
            service: svc,
            status,
            type,
            duration,
            label: svc,
            message,
            executions: row?.executions ?? [],
          },
        });
      }

      // In fallback mode, also include any service rows not in canonical list.
      if (!useCanonical && stepMap) {
        for (const [svc, { status, row }] of stepMap) {
          if (!svc || serviceNames.has(svc)) continue;
          serviceNames.add(svc);
          const type = typeByName.get(svc) ?? "";
          const duration = formatStepDuration(row) ?? null;
          const message = row?.message ?? "";
          inner.set(svc, { status });
          nodes.push({
            id: nodeId(phase, svc),
            data: {
              phase,
              service: svc,
              status,
              type,
              duration,
              label: svc,
              message,
              executions: row?.executions ?? [],
            },
          });
        }
      }
    }

    // ── Build edges ──────────────────────────────────────────────────────
    const edges = buildEdges(phases, nodeStatusMap);

    return {
      nodes,
      edges,
      phases,
      serviceNames: [...serviceNames],
      loading: false,
    };
  }, [pipelineState, templateServices, pipelineMode, intent]);
}
